import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Logo wall — Strapi sections.our-partners and sections.home-brands (both are
// a heading + a set of logo images). Centered header + a responsive logo grid.
export const logoWallSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().optional(),
  description: z.string().optional(),
  logos: z.array(z.string()).default([]),
});

type LogoWallProps = z.infer<typeof logoWallSchema>;

const Section = styled.section`
  max-width: 120rem;
  margin: 0 auto;
  padding: 5rem 1.6rem;

  @media (min-width: 64em) {
    padding: 7rem 2.4rem;
  }
`;

const Header = styled.div`
  max-width: 72rem;
  margin: 0 auto 3.2rem;
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.4rem, 3.5vw, 3.6rem);
`;

const Description = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 1.6rem;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: 48em) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 9rem;
  padding: 1.6rem;
  border: 1px solid #eef2fb;
  border-radius: 1.2rem;
  background: #ffffff;
`;

const Logo = styled.img`
  max-width: 80%;
  max-height: 60%;
  object-fit: contain;
`;

function LogoWall({ eyebrow, heading, description, logos }: LogoWallProps) {
  return (
    <Section>
      {eyebrow || heading || description ? (
        <Header>
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          {heading ? <Heading>{heading}</Heading> : null}
          {description ? <Description>{description}</Description> : null}
        </Header>
      ) : null}
      {logos.length > 0 ? (
        <Grid>
          {logos.map((src, i) => (
            <Cell key={`${src}-${i}`}>
              <Logo src={src} alt="" loading="lazy" />
            </Cell>
          ))}
        </Grid>
      ) : null}
    </Section>
  );
}

export const logoWallBlock: BlockDefinition<LogoWallProps> = {
  type: 'logoWall',
  schema: logoWallSchema,
  Renderer: LogoWall,
};
